import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  addPlayerToDraft,
  computeAccounting,
  copyPayloadToDraft,
  demoteStarterToBench,
  deriveFormation,
  draftToPayload,
  getBudgetRemaining,
  getDraftPlayerIds,
  getTotalCost,
  getTotalPlayerCount,
  promoteBenchToStarters,
  removePlayerFromDraft,
  squadToDraft,
} from '../squadUtils'
import { useSaveSquad } from '../queries'
import { EMPTY_DRAFT } from '../types'
import type { CopyPayload, DraftPlayer, MySquadResult, PlayerListItem, SquadDraft } from '../types'

type UseSquadPickerParams = {
  leagueId:     string
  savedSquad:   MySquadResult | undefined
  isWindowOpen: boolean
  selectedGwId: string | undefined
}

export function useSquadPicker({ leagueId, savedSquad, isWindowOpen, selectedGwId }: UseSquadPickerParams) {
  const [draft, setDraft] = useState<SquadDraft>(EMPTY_DRAFT)
  const initialized       = useRef(false)
  const prevGwIdRef       = useRef<string | undefined>(undefined)

  // Sync the draft when:
  //   1. The saved squad first loads (initial mount), or
  //   2. The selected gameweek changes (user navigates between GWs).
  // Do NOT re-sync on query re-validation within the same GW — that would wipe unsaved edits.
  useEffect(() => {
    const gwChanged = prevGwIdRef.current !== selectedGwId
    prevGwIdRef.current = selectedGwId

    // GW changed — reset initialized so the next data arrival always triggers a re-sync.
    // We cannot sync now because savedSquad may still hold data from the previous GW
    // while the new GW query is in flight.
    if (gwChanged) {
      initialized.current = false
    }

    if (savedSquad !== undefined && !initialized.current) {
      initialized.current = true
      setDraft(savedSquad.players.length > 0 ? squadToDraft(savedSquad) : EMPTY_DRAFT)
    }
  }, [savedSquad, selectedGwId])

  const saveMutation = useSaveSquad(leagueId)

  // ── Derived values ────────────────────────────────────────────────────────
  const formation       = useMemo(() => deriveFormation(draft.starters), [draft.starters])
  const totalCost       = useMemo(() => getTotalCost(draft), [draft])
  const budgetRemaining = useMemo(() => getBudgetRemaining(draft), [draft])
  const playerCount     = useMemo(() => getTotalPlayerCount(draft), [draft])
  const draftPlayerIds  = useMemo(() => getDraftPlayerIds(draft), [draft])
  const accounting      = useMemo(
    () => computeAccounting(draftPlayerIds, savedSquad?.baselineContext),
    [draftPlayerIds, savedSquad?.baselineContext],
  )
  const isComplete      = playerCount === 15 && formation.valid
  const canSave         = isComplete && draft.isDirty && isWindowOpen && !saveMutation.isPending

  // ── Actions ───────────────────────────────────────────────────────────────

  const addPlayer = useCallback((item: PlayerListItem) => {
    if (!isWindowOpen) return
    const player: DraftPlayer = {
      playerId:     item.playerId,
      name:         item.name,
      displayName:  item.displayName,
      position:     item.position,
      price:        item.price,
      teamCode:     item.teamCode,
      teamName:     item.teamName,
      isEliminated: item.isEliminated,
      gwPoints:     null,
    }
    setDraft(prev => addPlayerToDraft(prev, player))
  }, [isWindowOpen])

  const removePlayer = useCallback((playerId: string) => {
    if (!isWindowOpen) return
    setDraft(prev => removePlayerFromDraft(prev, playerId))
  }, [isWindowOpen])

  const reorderBench = useCallback((fromIndex: number, toIndex: number) => {
    if (!isWindowOpen) return
    setDraft(prev => {
      const bench = [...prev.bench]
      const [item] = bench.splice(fromIndex, 1)
      if (item === undefined) return prev
      bench.splice(toIndex, 0, item)
      return { ...prev, bench, isDirty: true }
    })
  }, [isWindowOpen])

  const swapStarterWithBench = useCallback((starterIndex: number, benchIndex: number) => {
    if (!isWindowOpen) return
    setDraft(prev => {
      const starters = [...prev.starters]
      const bench    = [...prev.bench]
      const starter  = starters[starterIndex]
      const bencher  = bench[benchIndex]
      if (starter === undefined || bencher === undefined) return prev
      starters[starterIndex] = bencher
      bench[benchIndex]      = starter
      return { ...prev, starters, bench, isDirty: true }
    })
  }, [isWindowOpen])

  const swapGks = useCallback(() => {
    if (!isWindowOpen) return
    setDraft(prev => {
      const gkIdx = prev.starters.findIndex(p => p.position === 'GK')
      if (gkIdx === -1 || !prev.benchGk) return prev
      const starters = [...prev.starters]
      const startingGk = starters[gkIdx]
      if (startingGk === undefined) return prev
      starters[gkIdx] = prev.benchGk
      return { ...prev, starters, benchGk: startingGk, isDirty: true }
    })
  }, [isWindowOpen])

  // Drop a bench player onto an empty pitch slot — promote into the XI (or swap with the
  // most over-subscribed starter when the XI is full). See promoteBenchToStarters.
  const promoteBench = useCallback((benchIndex: number) => {
    if (!isWindowOpen) return
    setDraft(prev => promoteBenchToStarters(prev, benchIndex))
  }, [isWindowOpen])

  // Drop a starter onto an empty outfield bench slot — demote into the bench (the reverse
  // of promoteBench). See demoteStarterToBench.
  const demoteStarter = useCallback((starterIndex: number, benchIndex: number) => {
    if (!isWindowOpen) return
    setDraft(prev => demoteStarterToBench(prev, starterIndex, benchIndex))
  }, [isWindowOpen])

  const swapStarters = useCallback((indexA: number, indexB: number) => {
    if (!isWindowOpen) return
    setDraft(prev => {
      const starters = [...prev.starters]
      const a = starters[indexA]
      const b = starters[indexB]
      if (a === undefined || b === undefined) return prev
      starters[indexA] = b
      starters[indexB] = a
      return { ...prev, starters, isDirty: true }
    })
  }, [isWindowOpen])

  // Stage a copy-preview payload into the local draft (§13.10 — no server write).
  // Sets isDirty=true so the standard save flow runs the save-time penalty check.
  const loadFromCopyPayload = useCallback((payload: CopyPayload) => {
    if (!isWindowOpen) return
    setDraft(copyPayloadToDraft(payload))
  }, [isWindowOpen])

  const resetDraft = useCallback(() => {
    if (savedSquad !== undefined && savedSquad.players.length > 0) {
      setDraft({ ...squadToDraft(savedSquad), isDirty: false })
    } else {
      setDraft(EMPTY_DRAFT)
    }
  }, [savedSquad])

  // Clear the whole squad — remove all 15 players from the draft. Marked dirty so the
  // change registers (and "Desfazer alterações" can revert it to the last saved squad).
  const clearDraft = useCallback(() => {
    if (!isWindowOpen) return
    setDraft({ ...EMPTY_DRAFT, isDirty: true })
  }, [isWindowOpen])

  const save = useCallback(() => {
    if (!canSave || savedSquad === undefined) return
    const payload = draftToPayload(draft)
    saveMutation.mutate(
      { squadId: savedSquad.squadId, payload },
      { onSuccess: () => setDraft(prev => ({ ...prev, isDirty: false })) },
    )
  }, [canSave, draft, savedSquad, saveMutation])

  return {
    draft,
    formation,
    totalCost,
    budgetRemaining,
    playerCount,
    draftPlayerIds,
    accounting,
    isComplete,
    canSave,
    isSaving: saveMutation.isPending,
    addPlayer,
    removePlayer,
    reorderBench,
    swapStarterWithBench,
    swapStarters,
    swapGks,
    promoteBench,
    demoteStarter,
    loadFromCopyPayload,
    resetDraft,
    clearDraft,
    save,
  }
}
